import type {
  AuthoritativeContinuitySnapshot,
  Chapter,
  ChapterPlan,
  Ledger,
  SoftStateBrief,
  StoryBible,
} from '../types';
import {
  AUTHORITATIVE_CONTINUITY_SNAPSHOT_CHAR_BUDGET,
  AUTHORITATIVE_CONTINUITY_SNAPSHOT_VERSION,
} from '../constants';
import { buildChapterCanonKnowledge, buildScribeCanonKnowledgeBlock } from './chapterCanonKnowledge.service';
import {
  formatLedgerArrayForPrompt,
  formatLedgerRecordForPrompt,
  formatResolvedFactsForPrompt,
} from './authoritativeLedgerMerge.service';
import { buildPriorChapterConsequences } from './latestStateDelta.service';
import {
  buildPhysicalContinuityContextSections,
  buildPhysicalContinuityLookbackSection,
} from './physicalContinuity.service';
import { AuthoritativeContinuitySnapshotBudgetError } from '../types';

type Priority = 1 | 2 | 3;

interface SnapshotItem {
  id: string;
  priority: Priority;
  text: string;
}

function hashSections(sections: AuthoritativeContinuitySnapshot['sections']): string {
  const payload = JSON.stringify(sections);
  let hash = 2166136261;
  for (let index = 0; index < payload.length; index += 1) {
    hash ^= payload.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(16, '0');
}

function compressText(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(0, maxChars - 20)).trim()}… [compressed]`;
}

function buildCharacterItems(ledger: Ledger): SnapshotItem[] {
  return Object.entries(ledger.characters || {}).map(([id, character]) => {
    const status = String((character as Record<string, unknown>).status || 'Active');
    const location = String((character as Record<string, unknown>).location || 'unknown');
    const injury = String((character as Record<string, unknown>).injury || (character as Record<string, unknown>).physicalState || '');
    const priority: Priority =
      status.toLowerCase().includes('active') || status.toLowerCase().includes('alive') ? 1 : 3;
    return {
      id,
      priority,
      text: `${formatLedgerRecordForPrompt(character)} | status=${status} | location=${location}${injury ? ` | limitation=${injury}` : ''}`,
    };
  });
}

function buildLocationItems(ledger: Ledger): SnapshotItem[] {
  return Object.entries(ledger.locations || {}).map(([id, location]) => {
    const status = String((location as Record<string, unknown>).status || 'Active');
    const priority: Priority = status.toLowerCase().includes('active') ? 1 : 3;
    return {
      id,
      priority,
      text: formatLedgerRecordForPrompt(location),
    };
  });
}

function buildInventoryItems(ledger: Ledger): SnapshotItem[] {
  return (ledger.inventory || []).map((entry, index) => ({
    id: `inventory_${index}`,
    priority: 1 as Priority,
    text: formatLedgerRecordForPrompt(entry),
  }));
}

function buildPlotThreadItems(ledger: Ledger): SnapshotItem[] {
  const unresolved = (ledger.plot_points || []).map((entry, index) => ({
    id: `plot_unresolved_${index}`,
    priority: 1 as Priority,
    text: formatLedgerRecordForPrompt(entry),
  }));
  const resolved = (ledger.resolved_plot_points || []).map((entry, index) => ({
    id: `plot_resolved_${index}`,
    priority: 3 as Priority,
    text: formatLedgerRecordForPrompt(entry),
  }));
  return [...unresolved, ...resolved];
}

function renderItems(items: SnapshotItem[], budgetChars: number): {
  text: string;
  compressedItemCount: number;
  omittedOptionalItemCount: number;
} {
  const priority1 = items.filter((item) => item.priority === 1);
  const priority2 = items.filter((item) => item.priority === 2);
  const priority3 = items.filter((item) => item.priority === 3);

  let compressedItemCount = 0;
  let omittedOptionalItemCount = 0;
  const lines: string[] = [];

  const appendItems = (bucket: SnapshotItem[], allowOmit: boolean) => {
    for (const item of bucket) {
      const line = `- ${item.text}`;
      if (lines.join('\n').length + line.length + 1 <= budgetChars) {
        lines.push(line);
        continue;
      }
      if (item.priority === 1) {
        const compressed = compressText(line, Math.max(80, budgetChars - lines.join('\n').length - 1));
        lines.push(compressed);
        compressedItemCount += 1;
        continue;
      }
      if (allowOmit) {
        omittedOptionalItemCount += 1;
        continue;
      }
      const compressed = compressText(line, 120);
      lines.push(compressed);
      compressedItemCount += 1;
    }
  };

  appendItems(priority1, false);
  appendItems(priority2, false);
  appendItems(priority3, true);

  const priority1Text = priority1.map((item) => `- ${item.text}`).join('\n');
  if (priority1Text.length > budgetChars) {
    throw new AuthoritativeContinuitySnapshotBudgetError({
      chapterNumber: 0,
      requiredChars: priority1Text.length,
      budgetChars,
    });
  }

  return {
    text: lines.join('\n') || 'None.',
    compressedItemCount,
    omittedOptionalItemCount,
  };
}

export function logAuthoritativeContinuitySnapshot(payload: Record<string, unknown>): void {
  console.log(`[AUTHORITATIVE CONTINUITY SNAPSHOT] ${JSON.stringify(payload)}`);
}

export function buildAuthoritativeContinuitySnapshot(params: {
  ledger: Ledger;
  storyBible: StoryBible;
  chapterNumber: number;
  plan?: ChapterPlan;
  softState?: SoftStateBrief | null;
  previousChapters?: Chapter[];
}): AuthoritativeContinuitySnapshot {
  const { ledger, storyBible, chapterNumber, plan, softState, previousChapters } = params;
  const canonKnowledge = buildChapterCanonKnowledge({
    ledger,
    storyBible,
    plan,
    chapterNumber,
  });
  const physicalSections = buildPhysicalContinuityContextSections(ledger, plan, chapterNumber);
  const physicalLookback = buildPhysicalContinuityLookbackSection(ledger, plan, chapterNumber);
  const priorConsequences = buildPriorChapterConsequences(ledger.latestStateDelta);
  const delta = ledger.latestStateDelta;

  const characterRender = renderItems(buildCharacterItems(ledger), 20_000);
  const locationRender = renderItems(buildLocationItems(ledger), 10_000);
  const inventoryRender = renderItems(buildInventoryItems(ledger), 8_000);
  const plotRender = renderItems(buildPlotThreadItems(ledger), 12_000);

  const latestStateDeltaSection = delta
    ? [
        `Chapter ${delta.chapterNumber} delta (${delta.generatedAt}):`,
        ...delta.consequences.map((entry) => `- ${entry}`),
        ...delta.characterChanges.map((entry) => `- CHARACTER: ${entry.summary}`),
        ...delta.physicalObjectChanges.map((entry) => `- PHYSICAL: ${entry.summary}`),
        ...delta.obligationsOpened.map((entry) => `- OPENED: ${entry.summary}`),
        ...delta.obligationsResolved.map((entry) => `- RESOLVED: ${entry.summary}`),
      ].join('\n')
    : 'No prior chapter delta recorded yet.';

  const sections = {
    authoritativeStoryState: [
      `Momentum: ${ledger.momentum || 'unknown'}`,
      `Arc stage: ${ledger.arc_stage || 'unknown'}`,
      `Current time: ${ledger.current_time || 'unknown'}`,
      formatResolvedFactsForPrompt(ledger),
    ].filter(Boolean).join('\n'),
    latestStateDelta: latestStateDeltaSection,
    priorChapterConsequences: priorConsequences.length
      ? priorConsequences.map((entry) => `- ${entry}`).join('\n')
      : 'No immediate prior consequences recorded.',
    openContinuityObligations: physicalSections.openObligations || 'No open obligations.',
    currentRelevantPhysicalState: physicalSections.currentState || 'No relevant physical objects.',
    activeCharacterStates: characterRender.text,
    currentAndRecentLocations: locationRender.text,
    relevantInventoryAndEquipment: inventoryRender.text,
    activePlotThreads: plotRender.text,
    canonLocks: buildScribeCanonKnowledgeBlock(canonKnowledge),
    characterKnowledgeLimits: [
      buildScribeCanonKnowledgeBlock(canonKnowledge),
      physicalLookback,
      softState?.antiRepetitionWarnings ? `ANTI-REPETITION: ${softState.antiRepetitionWarnings}` : '',
    ].filter(Boolean).join('\n'),
  };

  const promptText = [
    'AUTHORITATIVE STORY STATE',
    sections.authoritativeStoryState,
    '',
    'LATEST STATE DELTA',
    sections.latestStateDelta,
    '',
    'PRIOR-CHAPTER CONSEQUENCES',
    sections.priorChapterConsequences,
    '',
    'OPEN CONTINUITY OBLIGATIONS',
    sections.openContinuityObligations,
    '',
    'CURRENT RELEVANT PHYSICAL STATE',
    sections.currentRelevantPhysicalState,
    '',
    'ACTIVE CHARACTER STATES',
    sections.activeCharacterStates,
    '',
    'CURRENT AND RECENT LOCATIONS',
    sections.currentAndRecentLocations,
    '',
    'RELEVANT INVENTORY AND EQUIPMENT',
    sections.relevantInventoryAndEquipment,
    '',
    'ACTIVE PLOT THREADS',
    sections.activePlotThreads,
    '',
    'CANON LOCKS',
    sections.canonLocks,
    '',
    'CHARACTER KNOWLEDGE LIMITS',
    sections.characterKnowledgeLimits,
    previousChapters?.length
      ? `\nRECENT CHAPTER SUMMARIES:\n${previousChapters
          .slice(-3)
          .map((chapter) => `Chapter ${chapter.id}: ${chapter.summary || 'No summary.'}`)
          .join('\n')}`
      : '',
  ].join('\n');

  if (promptText.length > AUTHORITATIVE_CONTINUITY_SNAPSHOT_CHAR_BUDGET) {
    const requiredOnly = [
      sections.authoritativeStoryState,
      sections.latestStateDelta,
      sections.priorChapterConsequences,
      sections.openContinuityObligations,
      sections.currentRelevantPhysicalState,
      sections.activeCharacterStates,
      sections.currentAndRecentLocations,
      sections.relevantInventoryAndEquipment,
      sections.activePlotThreads,
      sections.canonLocks,
      sections.characterKnowledgeLimits,
    ].join('\n');
    if (requiredOnly.length > AUTHORITATIVE_CONTINUITY_SNAPSHOT_CHAR_BUDGET) {
      throw new AuthoritativeContinuitySnapshotBudgetError({
        chapterNumber,
        requiredChars: requiredOnly.length,
        budgetChars: AUTHORITATIVE_CONTINUITY_SNAPSHOT_CHAR_BUDGET,
      });
    }
  }

  const snapshotHash = hashSections(sections);
  const snapshot: AuthoritativeContinuitySnapshot = {
    chapterNumber,
    snapshotVersion: AUTHORITATIVE_CONTINUITY_SNAPSHOT_VERSION,
    snapshotHash,
    sourceLedgerVersion: ledger.last_updated || 'unknown',
    latestDeltaChapter: delta?.chapterNumber || 0,
    sectionCounts: {
      characters: Object.keys(ledger.characters || {}).length,
      locations: Object.keys(ledger.locations || {}).length,
      inventory: (ledger.inventory || []).length,
      plotThreads: (ledger.plot_points || []).length,
      obligations: (ledger.physical_continuity_obligations || []).filter((entry) => entry.status === 'OPEN').length,
      physicalObjects: (ledger.continuity_objects || []).length,
      consequences: priorConsequences.length,
    },
    requiredItemCount:
      characterRender.text.split('\n').length +
      locationRender.text.split('\n').length +
      inventoryRender.text.split('\n').length +
      plotRender.text.split('\n').length,
    compressedItemCount:
      characterRender.compressedItemCount +
      locationRender.compressedItemCount +
      inventoryRender.compressedItemCount +
      plotRender.compressedItemCount,
    omittedOptionalItemCount:
      characterRender.omittedOptionalItemCount +
      locationRender.omittedOptionalItemCount +
      inventoryRender.omittedOptionalItemCount +
      plotRender.omittedOptionalItemCount,
    sections,
    promptText,
  };

  logAuthoritativeContinuitySnapshot({
    chapterNumber,
    snapshotVersion: snapshot.snapshotVersion,
    snapshotHash: snapshot.snapshotHash,
    sourceLedgerVersion: snapshot.sourceLedgerVersion,
    sectionCounts: snapshot.sectionCounts,
    latestDeltaChapter: snapshot.latestDeltaChapter,
    requiredItemCount: snapshot.requiredItemCount,
    compressedItemCount: snapshot.compressedItemCount,
    omittedOptionalItemCount: snapshot.omittedOptionalItemCount,
  });

  return snapshot;
}

export function renderAuthoritativeContinuityPrompt(snapshot: AuthoritativeContinuitySnapshot): string {
  return snapshot.promptText;
}

export function estimateSnapshotTokens(snapshot: AuthoritativeContinuitySnapshot): number {
  return Math.ceil(snapshot.promptText.length / 4);
}

export function measureSnapshotForLedger(params: {
  ledger: Ledger;
  storyBible: StoryBible;
  chapterNumber: number;
}): {
  recordCounts: AuthoritativeContinuitySnapshot['sectionCounts'];
  serializedCharacterLength: number;
  estimatedTokens: number;
  compressionActions: number;
  priority1Retained: number;
  optionalOmitted: number;
} {
  const snapshot = buildAuthoritativeContinuitySnapshot({
    ledger: params.ledger,
    storyBible: params.storyBible,
    chapterNumber: params.chapterNumber,
  });
  return {
    recordCounts: snapshot.sectionCounts,
    serializedCharacterLength: snapshot.promptText.length,
    estimatedTokens: estimateSnapshotTokens(snapshot),
    compressionActions: snapshot.compressedItemCount,
    priority1Retained: snapshot.requiredItemCount,
    optionalOmitted: snapshot.omittedOptionalItemCount,
  };
}
